import PropTypes from 'prop-types';

// material-ui
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

// project imports
import useProviderStatus from './useProviderStatus';
import { providerChipLabel, providerColor } from './model';

export default function ProviderStatus() {
  const { enabled, providers, loading, error } = useProviderStatus();
  const visibleProviders = providers;

  if (!enabled) {
    return null;
  }

  if (loading && !visibleProviders.length) {
    return (
      <StatusFrame>
        <CircularProgress size={14} />
      </StatusFrame>
    );
  }

  if (error) {
    return (
      <StatusFrame>
        <Chip size="small" color="warning" variant="outlined" label="Offline" />
      </StatusFrame>
    );
  }

  if (!visibleProviders.length) return null;

  return (
    <StatusFrame>
      {visibleProviders.map((provider) => (
        <Tooltip key={provider.provider} title={`${provider.label}: ${provider.display}`}>
          <Chip
            size="small"
            color={providerColor(provider)}
            variant={provider.attention_required ? 'filled' : 'outlined'}
            label={providerChipLabel(provider)}
            sx={{
              flexShrink: 0,
              minWidth: 40,
              height: 24,
              '& .MuiChip-label': {
                px: 1,
                fontWeight: 700
              }
            }}
          />
        </Tooltip>
      ))}
    </StatusFrame>
  );
}

function StatusFrame({ children }) {
  return (
    <Stack
      direction="row"
      aria-label="Provider connection status"
      sx={{
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 0.75,
        minWidth: 0,
        maxWidth: { xs: 220, md: 420, xl: 560 },
        overflow: 'hidden'
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
        Platform status:
      </Typography>
      {children}
    </Stack>
  );
}

StatusFrame.propTypes = { children: PropTypes.node.isRequired };
